import { useEffect, useState } from 'react';
import TeamCard from './TeamCard';
import type { IMyTeam } from '../types/myTeam';

const PAGE_SIZE = 12;

interface MyTeamListProps {
  teams: IMyTeam[];
  onCreateTeam: () => void;
}

function MyTeamList({ teams, onCreateTeam }: MyTeamListProps) {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(teams.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedTeams = teams.slice(startIndex, startIndex + PAGE_SIZE);

  useEffect(() => {
    setCurrentPage(1);
  }, [teams.length]);

  const getPageNumbers = (): (number | 'ellipsis')[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | 'ellipsis')[] = [1];
    if (currentPage > 3) pages.push('ellipsis');
    const midStart = Math.max(2, currentPage - 1);
    const midEnd = Math.min(totalPages - 1, currentPage + 1);
    for (let i = midStart; i <= midEnd; i++) {
      if (i !== 1 && i !== totalPages) pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push('ellipsis');
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  return (
    <div className="myteam-list">
      <div className="myteam-list-header">
        <h1 className="myteam-list-title">My Teams</h1>
        <button type="button" className="myteam-create-btn" onClick={onCreateTeam}>
          Create a team
        </button>
      </div>
      <div className="myteam-team-grid-wrap">
        <div className="myteam-team-grid">
          {paginatedTeams.map((team) => (
            <TeamCard key={team.teamId} team={team} />
          ))}
        </div>
        {totalPages > 1 && (
          <div className="myteam-pagination">
            <div className="myteam-pagination-info">
              Showing {startIndex + 1}–{Math.min(startIndex + PAGE_SIZE, teams.length)} of {teams.length} teams
            </div>
            <button
              type="button"
              className="myteam-pagination-btn"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              Previous
            </button>
            <div className="myteam-pagination-pages">
              {getPageNumbers().map((page, i) =>
                page === 'ellipsis' ? (
                  <span key={`ellipsis-${i}`} className="myteam-pagination-ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={page}
                    type="button"
                    className={`myteam-pagination-page ${currentPage === page ? 'active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                )
              )}
            </div>
            <button
              type="button"
              className="myteam-pagination-btn"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MyTeamList;
